import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { PetEntity } from 'src/game/logic/EntityManager';
import { PetSpeciesID } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { gsap } from 'gsap';
import { user } from 'src/StateObserver';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { animDuration, delay } from 'src/lib/utils';
import { GemsWallet } from 'src/game/components/GemsWallet';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { AssetGroup } from '@play-co/timestep-core';
import { movesPinchCheck } from 'src/lib/pinch';
import Match2AchievementItem, {
  match2AchievementTypes,
} from '../components/Match2AchievementItem';
import {
  getMatch2CloseButtonX,
  match2CloseButtonY,
} from '../../components/match2PopupCloseButton';

type CloseHandler = (result: boolean) => void;
const ui = uiConfig.buruburu.care.confirmation;

type PopupMatch2MovePinchAchievementsOpts = {
  price: number;
  frenzy?: number;
  dailyChallenge?: number;
  race?: number;
  fujiQuest?: number;
  treasureExpress?: number;
  league?: number;
};

export default class PopupMatch2MovePinchAchievements extends PopupBasicLazy {
  private close: CloseHandler;
  private contentWrapper: View;
  private ctaButton: ButtonScaleViewWithText;
  private gemsWallet: GemsWallet;
  private pet: PetEntity;
  private species: PetSpeciesID;
  private bubble: ImageScaleView;
  private achievements: FlexBoxView;
  private toggleMessage?: LangBitmapFontTextShadowView;
  private isPopupHidden = false;

  public getAssetsGroup() {
    return new AssetGroup([
      ...AssetGroup.constructURLs(['assets/ui/match2/common/']),
    ]);
  }

  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = 676;
    const height = 574;

    super({
      superview: opts.superview,
      close: () => opts.close(false),
      ...ui.popup,
      darkerBg: true,
      offsetY: -50,
      width,
      height,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_POPUP,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();
    this.message.removeFromSuperview();
    this.title.title.updateOpts({ color: '#E25959' });
    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.95)',
    });
    this.bg.onClick = () => this.toggleShowPuzzle();

    // Create gems header panel
    this.gemsWallet = new GemsWallet({
      superview: this.root,
      tracking: { feature: 'header', $subFeature: 'buy-gems' },
    });

    // Create close button
    new ButtonScaleView({
      ...uiConfig.buttons.close,
      superview: this.box,
      x: getMatch2CloseButtonX(this.box.style.width),
      y: match2CloseButtonY,
      onClick: async () => {
        sounds.playSound('cancel');
        return opts.close(false);
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_BACK,
      },
    });

    // Create a wrapper view for the content
    this.contentWrapper = new View({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
    });

    // Create animated pet avatar
    this.species = Pet.getters.getCurrentEvolution(user())?.species ?? 'ball';
    this.pet = new PetEntity('minigame', this.species);
    this.pet.updateOpts({
      superview: this.contentWrapper,
      x: 120,
      y: 365,
      scale: 0.75,
      anchorX: 0.5 * this.pet.style.width,
      anchorY: 0.78 * this.pet.style.height,
    });

    // Create speech bubble
    this.bubble = new ImageScaleView({
      superview: this.contentWrapper,
      image: `assets/ui/conversation/speechbubble.png`,
      imgScale: 0.66,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 44, right: 44 },
        vertical: { top: 44, bottom: 44 },
      },
      width: 423,
      height: 309,
      x: 220,
      y: 100,
    });

    // Create speech bubble tail
    new ImageView({
      superview: this.bubble,
      image: 'assets/ui/conversation/speechbubble_tail.png',
      width: 82,
      height: 67,
      y: 200,
      x: 10,
      r: Math.PI / 2,
      scale: 0.5,
    });

    // Create speech bubble text
    new LangBitmapFontTextView({
      superview: this.bubble,
      size: 32,
      font: 'Body',
      color: '#985C25',
      localeText: () => i18n('match2.movesPinch.messageGeneric2'),
      width: this.bubble.style.width,
      height: 85,
      y: 12,
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
    });

    // Create achievement items container
    this.achievements = new FlexBoxView({
      superview: this.bubble,
      width: this.bubble.style.width - 20,
      height: this.bubble.style.height - 140,
      flex: {
        alignContent: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
      },
      x: 10,
      y: 105,
    });

    // Create CTA button
    this.ctaButton = new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: this.contentWrapper,
      width: 477,
      height: 110,
      centerOnOrigin: true,
      x: width * 0.5,
      y: height - 95,
      fontSize: 50,
      icon: {
        image: 'assets/ui/hud/icons/hud_gem.png',
        width: 54,
        height: 45,
        offset: { x: 190, y: 0 },
      },
      labelOffsetX: -35,
      localeText: () => i18n('match2.movesPinch.cta'),
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_OK,
      },
    });

    // Create floating toggle message
    this.toggleMessage = new LangBitmapFontTextShadowView({
      superview: this.root,
      font: 'Body',
      color: '#FFFFFF',
      width: this.root.style.width,
      height: 100,
      size: 30,
      x: this.root.style.width * 0.5,
      y: 150 + this.box.style.y + this.box.style.height * 0.5,
      localeText: () => i18n('match2.movesPinch.toggleMessage1'),
      centerOnOrigin: true,
      centerAnchor: true,
      verticalAlign: 'center',
      align: 'center',
      scaleY: 0,
      blockEvents: true,
      backgroundColor: 'rgba(0,0,0,0.7)',
    });
  }

  public init(args: PopupMatch2MovePinchAchievementsOpts) {
    super.init({ title: i18n(`match2.movesPinch.titleGeneric`) });

    // Update the pet avatar animation
    const petAnimations = petAnimationNames[this.species];
    this.pet.setAnimation(petAnimations.cry);

    const { price } = args;

    // Update CTA button label and onClick handler
    this.ctaButton.label.localeText = () =>
      `${i18n('match2.movesPinch.cta')}  ${price}`;
    this.ctaButton.onClick = async () => {
      sounds.sfx.play('ok');
      const success = await movesPinchCheck(price, 'match2_shop_moves');
      if (success) this.close(true);
    };

    this.addAchievements(args);
  }

  private addAchievements(args: PopupMatch2MovePinchAchievementsOpts) {
    const total = match2AchievementTypes.filter((type) => args[type]).length;
    let size = 90;

    // Adjust item size based on the number of achievements
    switch (total) {
      case 1:
        size = 140;
        break;
      case 2:
        size = 125;
        break;
      case 3:
        size = 110;
        break;
    }

    // Create achievement items based on the provided args
    for (let index = 0; index < match2AchievementTypes.length; index++) {
      const type = match2AchievementTypes[index];
      if (!args[type]) continue;
      new Match2AchievementItem({
        superview: this.achievements,
        width: size,
        height: size,
        type: type,
        count: args[type] as number,
      });
    }
  }

  private async toggleShowPuzzle() {
    if (!this.toggleMessage) return;
    this.isPopupHidden = !this.isPopupHidden;

    // Toggle CTAs interactivity accordingly
    this.box.updateOpts({ blockEvents: this.isPopupHidden });

    const duration = 0.5;
    gsap.to(this.bg.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });
    gsap.to(this.box.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });

    await this.tweenToggleMessageScaleY(0);
    this.toggleMessage.localeText = () =>
      i18n(`match2.movesPinch.toggleMessage${this.isPopupHidden ? 2 : 1}`);
    await this.tweenToggleMessageScaleY(1);
  }

  private async tweenToggleMessageScaleY(v: number) {
    if (!this.toggleMessage) return;
    await gsap.to(this.toggleMessage.style, {
      duration: 0.15,
      scaleY: v,
      ease: 'quad.inOut',
      overwrite: true,
    });
  }

  public override async fadeIn() {
    super.fadeIn();
    await delay(animDuration * 2);
    await this.tweenToggleMessageScaleY(1);
  }

  public override async fadeOut() {
    void this.tweenToggleMessageScaleY(0);
    super.fadeOut();
  }
}
